import {
  AnalyticsActiveUsers,
  AnalyticsRequests,
} from "@apis:analytics/analytics";
import { GridColDef } from "@mui/x-data-grid";
import { DateTime } from "luxon";

export enum RangeType {
  LastSevenDays = "Last 7 days",
  LastThirtyDays = "Last 30 days",
  LastSixMonths = "Last 6 months",
  LastYear = "Last year",
  Custom = "Custom",
}

export const RequestTypeList = [
  {
    value: "all",
    label: "All Requests",
  },
  {
    value: "nemoSearch",
    label: "Nemo Search",
  },
  {
    value: "nemoDocs",
    label: "Nemo Docs",
  },
];

export const getRangeValues = () => {
  const today = DateTime.local();
  // Include today
  const tomorrow = today.plus({ days: 1 });
  return {
    [RangeType.LastSevenDays]: [
      tomorrow.minus({ days: 7 }).toISODate(),
      today.toISODate(),
    ],
    [RangeType.LastThirtyDays]: [
      tomorrow.minus({ days: 30 }).toISODate(),
      today.toISODate(),
    ],
    [RangeType.LastSixMonths]: [
      tomorrow.minus({ months: 6 }).toISODate(),
      today.toISODate(),
    ],
    [RangeType.LastYear]: [
      tomorrow.minus({ year: 1 }).toISODate(),
      today.toISODate(),
    ],
    [RangeType.Custom]: [undefined, undefined],
  };
};

export const AccountColumns: GridColDef[] = [
  {
    field: "fullName",
    headerName: "User Name",
    flex: 1,
  },
  {
    field: "numRequests",
    headerName: "Number of Requests",
    flex: 1,
  },
];

export const ReferencedDocumentColumns: GridColDef[] = [
  {
    field: "document",
    headerName: "Document Name",
    flex: 1,
  },
  {
    field: "numReferences",
    headerName: "Number of References",
    flex: 1,
  },
];

type FromDate = string;
type ToDate = string;

enum TimeUnit {
  Day = "day",
  Week = "week",
  Month = "month",
  Quarter = "quarter",
  Year = "year",
}

const formatTimeUnit = (timeUnit: TimeUnit) => {
  if (timeUnit === TimeUnit.Day) return "yyyy-MM-dd";
  if (timeUnit === TimeUnit.Week) return "kkkk - 'W'WW";
  if (timeUnit === TimeUnit.Month) return "yyyy-MM";
  if (timeUnit === TimeUnit.Quarter) return "yyyy - 'Q'q";
  if (timeUnit === TimeUnit.Year) return "yyyy";
  return "yyyy-MM-dd";
};

const getRangesTimeUnit = (timeUnit: TimeUnit, date: string) => {
  if (timeUnit === TimeUnit.Day) return { from: date, to: date };
  return {
    from: DateTime.fromISO(date).startOf(timeUnit).toISODate(),
    to: DateTime.fromISO(date).endOf(timeUnit).toISODate(),
  };
};

const whatTimeUnit = (fromDate: FromDate, toDate: ToDate) => {
  const from = DateTime.fromISO(fromDate);
  const to = DateTime.fromISO(toDate);

  if (to.diff(from, "years").years >= 7) return TimeUnit.Year;
  if (to.diff(from, "quarters").quarters >= 3) return TimeUnit.Quarter;
  if (to.diff(from, "months").months >= 3) return TimeUnit.Month;
  if (to.diff(from, "weeks").weeks >= 3) return TimeUnit.Week;
  return TimeUnit.Day;
};

const generateRangeDates = (dates: string[], timeUnit: TimeUnit) => {
  const ranges: {
    shortLabel: string;
    timeUnit: TimeUnit;
    start: string;
    end: string;
  }[] = [];

  const trackingLabel = new Set<string>();

  for (const element of dates) {
    const { from, to } = getRangesTimeUnit(timeUnit, element);
    const label = DateTime.fromISO(element).toFormat(formatTimeUnit(timeUnit));
    if (trackingLabel.has(label)) continue;
    ranges.push({
      timeUnit,
      start: from as string,
      end: to as string,
      shortLabel: label,
    });
    trackingLabel.add(label);
  }

  return ranges;
};

const getPeriodTime = (fromDate: FromDate, toDate: ToDate) => {
  const from = DateTime.fromISO(fromDate);
  const to = DateTime.fromISO(toDate);
  const timeUnit = whatTimeUnit(fromDate, toDate);

  const mappingUnit = {
    [TimeUnit.Day]: "days",
    [TimeUnit.Week]: "weeks",
    [TimeUnit.Month]: "months",
    [TimeUnit.Quarter]: "quarters",
    [TimeUnit.Year]: "years",
  };

  const dates: string[] = [];
  let currentDate = from;
  while (currentDate <= to) {
    dates.push(currentDate.toISODate() as string);
    currentDate = currentDate.plus({ [mappingUnit[timeUnit]]: 1 });

    /*
     * Another time unit is range of time not a single date, so we have to add toDate to make sure
     * the last date is included in the range
     */
    if (currentDate > to && timeUnit !== TimeUnit.Day) {
      dates.push(to.toISODate() as string);
    }
  }

  return generateRangeDates(dates, timeUnit);
};

const isSeriesZero = (series: number[]) => {
  return series.every((item) => item === 0);
};

export const generateActiveUsersChart = (
  from: FromDate,
  to: ToDate,
  activeUsersPeriodData?: AnalyticsActiveUsers[],
) => {
  const periodTime = getPeriodTime(from, to);

  const xAxisData = periodTime.map((label) => {
    return label.shortLabel;
  });

  const activeUsers = periodTime.map(({ start, end, timeUnit }) => {
    const activeUser = activeUsersPeriodData
      ?.filter((item) => {
        if (timeUnit === TimeUnit.Day) return item.date === start;
        return item.date >= start && item.date < end;
      })
      .map((item) => item.numLogins);
    return activeUser?.reduce((acc, item) => acc + item, 0) ?? 0;
  });

  return {
    xAxis: [
      {
        id: "date",
        data: xAxisData,
        scaleType: "point" as const,
      },
    ],
    yAxis: [
      {
        min: 0,
        /*
         * If both series are zero, we need to set max to specific value to make sure x-axis and y-axis start at
         * the same point
         */
        max: isSeriesZero(activeUsers) ? 3 : undefined,
      },
    ],
    series: [
      {
        id: "activeUsers",
        label: "Active users",
        data: activeUsers,
        color: "#1FADB2",
      },
    ],
  };
};

export const generateRequestsChart = (
  from: FromDate,
  to: ToDate,
  requestsPeriodData?: AnalyticsRequests[],
) => {
  const periodTime = getPeriodTime(from, to);

  const xAxisData = periodTime.map((label) => {
    return label.shortLabel;
  });

  const requests = periodTime.map(({ start, end, timeUnit }) => {
    const request = requestsPeriodData
      ?.filter((item) => {
        if (timeUnit === TimeUnit.Day) return item.date === start;
        return item.date >= start && item.date < end;
      })
      .map((item) => item.numRequests);
    return request?.reduce((acc, item) => acc + item, 0) ?? 0;
  });

  return {
    xAxis: [
      {
        id: "date",
        data: xAxisData,
        scaleType: "point" as const,
      },
    ],
    yAxis: [
      {
        min: 0,
        /*
         * If both series are zero, we need to set max to specific value to make sure x-axis and y-axis start at
         * the same point
         */
        max: isSeriesZero(requests) ? 3 : undefined,
      },
    ],
    series: [
      {
        id: "numsOfRequests",
        label: "Number of requests",
        data: requests,
        color: "#1FADB2",
      },
    ],
  };
};
